import {
  Box,
  Group,
  Progress,
  Rating,
  Text,
  Image,
  Button,
  Title,
  Modal,
  Textarea,
  useMantineTheme,
  Flex,
  LoadingOverlay,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { orderStatusText } from '../../../config/Constant';
import { useOrderStyle, useWebOrderStyle } from '../style';
import { buttonStyle } from '../../../utils/styles/GlobalStyle';
import { ModalCloseButton } from '../../Cart/style';
import { RatingIcon, stepBack, stepBackColor, stepForward, stepForwardColor } from '../../../assets';
import { addUpdateReview } from '../../../services/review/review.service';
import { toastSuccess } from '../../../config/Helper';
import { useParams } from 'react-router-dom';
import { customLoader } from '../../../components/CustomLoader';

const Review = () => {
  const theme = useMantineTheme();
  const mobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const { classes } = useOrderStyle();
  const { classes: webOrderStyle } = useWebOrderStyle();
  const { classes: buttonClass } = buttonStyle();
  const { classes: closeButtonStyle } = ModalCloseButton();
  const maxStep = 3;
  const minStep = 1;
  const initialProgress = 100 / 3;
  const [ratingValue, setRatingValue] = useState<number>(0);
  const [progressValue, setProgressValue] = useState(initialProgress);
  const [activeStep, setActiveStep] = useState<number>(1);
  const [statusText, setStatusText] = useState(orderStatusText[minStep - 1]);
  const [opened, { open, close }] = useDisclosure(false);
  const [review, setReview] = useState<string>('');
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const { orderId } = useParams<{ orderId: string }>();
  const [loading, setLoading] = useState<boolean>(false);

  const progressDecrement = (step: number) => {
    if (step >= minStep) {
      setActiveStep(step);
      setProgressValue(progressValue - initialProgress);
      setStatusText(orderStatusText[step - 1]);
    } else {
      setProgressValue(initialProgress);
      setActiveStep(minStep);
    }
  };

  const progressIncrement = (step: number) => {
    if (step <= maxStep) {
      setActiveStep(step);
      setProgressValue(progressValue + initialProgress);
      setStatusText(orderStatusText[step - 1]);
    } else {
      setProgressValue(initialProgress * maxStep);
      setActiveStep(maxStep);
    }
  };

  const reviewText = (value: any) => {
    setReview(value);
    if (value.length > 20) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  };

  const submitReview = async () => {
    setLoading(true);
    let formData = {
      review_type: activeStep,
      ...(ratingValue === 0 ? { comment: review } : { rating: ratingValue }),
    };
    try {
      const data = await addUpdateReview(orderId, formData);
      if (data.success) {
        toastSuccess(data.message);
        setLoading(false);
        setRatingValue(0);
        close();
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ratingValue > 1) {
      submitReview();
    }
  }, [ratingValue]);

  return (
    <>
      <LoadingOverlay visible={loading} pos='fixed' loader={customLoader} />
      <Box pt={'65px'} pl={'10px'}>
        <Text fz={'16px'} ta='left' pb={'sm'} color='red' fw={500}>
          {statusText}
        </Text>
        <Rating
          size={'55px'}
          ta='center'
          pb={'sm'}
          emptySymbol={<RatingIcon height='55px' width='55px' style={{ paddingLeft: '10px' }} />}
          highlightSelectedOnly={false}
          value={ratingValue}
          onChange={setRatingValue}
          color='yellow'
        />
        <Progress value={progressValue} radius='sm' size={'md'} color='red' />
        <Flex direction='row' gap={15} align='center' pt={16} justify='space-between'>
          <Button
            sx={{ '&[data-disabled]': { background: '#fff' } }}
            disabled={activeStep === minStep}
            onClick={() => {
              progressDecrement(activeStep - 1);
            }}
            p={0}
            variant={'outline'}
            style={{ border: 'none' }}>
            <Image width='auto' height='auto' src={activeStep === minStep ? stepBack : stepBackColor} />
          </Button>
          <Title fz={mobile ? '11px' : '14px'} color='black' fw={500}>
            Please click this{' '}
            <Text onClick={open} style={{ cursor: 'pointer' }} span c='#FA9418' inherit>
              link
            </Text>{' '}
            to write a detailed review
          </Title>
          <Button
            disabled={activeStep === maxStep}
            sx={{ '&[data-disabled]': { background: '#fff' } }}
            onClick={() => {
              progressIncrement(activeStep + 1);
            }}
            p={0}
            variant={'outline'}
            style={{ border: 'none' }}>
            <Image width='auto' height='auto' src={activeStep === maxStep ? stepForward : stepForwardColor} />
          </Button>
        </Flex>
        <Box pt={'25px'} pb={'15px'}>
          <Text fw={500} align='center' fz={mobile ? '16px' : '18px'} color={'red'}>
            {activeStep === maxStep ? 'Thank you for rating!' : ''}
          </Text>
        </Box>
      </Box>

      <Modal
        classNames={classes}
        className={closeButtonStyle.closeButton}
        opened={opened}
        onClose={close}
        title='Review'
        centered>
        <Text fz={mobile ? '11px' : '13px'} color='black' fw={500} pt='10px'>
          Please write a few words to help us serve you better
        </Text>
        <Textarea
          placeholder='Type in within 100 words'
          label='Valuable Comment'
          autosize
          minRows={5}
          pt={'md'}
          classNames={mobile ? classes : webOrderStyle}
          onChange={(e) => {
            reviewText(e.target.value);
          }}
        />
        {buttonDisabled && (
          <Text c='red.6' fz={12} fw={450}>
            Comment should be at least 20 characters long.
          </Text>
        )}
        <Group pt={'15px'} position='center'>
          <Button
            disabled={buttonDisabled}
            onClick={submitReview}
            size='xl'
            px={50}
            className={buttonClass.buttonShadow}
            fz={'16px'}
            color='red'
            radius={'11px'}
            sx={{
              '&[data-disabled]': { opacity: 0.4, backgroundColor: 'red' },
            }}>
            Submit
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default Review;
